"use client";

import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert,
  Flame,
  Activity,
  Heart,
  Smile,
  AlertTriangle,
  UserCheck,
  CheckCircle2,
  Calendar,
  Sparkles,
  Info,
} from "lucide-react";

interface AgentStats {
  totalTasks: number;
  highPriorityTasks: number;
  pendingTasks: number;
  kudosReceived: number;
}

interface Agent {
  id: string;
  name: string;
  role: string;
  stats: AgentStats;
}

interface BurnoutClientProps {
  initialAgents: Agent[];
}

export function BurnoutClient({ initialAgents }: BurnoutClientProps) {
  // Setup fallback simulated human agents if database is empty
  const agentsList =
    initialAgents.length > 0
      ? initialAgents
      : [
          {
            id: "agent_h1",
            name: "Alessandro Rossi",
            role: "Lead Fullstack Dev",
            stats: {
              totalTasks: 12,
              highPriorityTasks: 5,
              pendingTasks: 6,
              kudosReceived: 4,
            },
          },
          {
            id: "agent_h2",
            name: "Giulia Bianchi",
            role: "UX/UI Designer",
            stats: {
              totalTasks: 4,
              highPriorityTasks: 1,
              pendingTasks: 2,
              kudosReceived: 8,
            },
          },
          {
            id: "agent_h3",
            name: "Marco Verdi",
            role: "Product Manager",
            stats: {
              totalTasks: 8,
              highPriorityTasks: 4,
              pendingTasks: 5,
              kudosReceived: 1,
            },
          },
        ];

  const [selectedAgentId, setSelectedAgentId] = useState<string>(
    agentsList[0]?.id || "",
  );
  const selectedAgent =
    agentsList.find((a) => a.id === selectedAgentId) || agentsList[0];

  // Stress indicator states for each agent (keyed by agent ID) dynamically initialized with realistic, deterministic values
  const [indicators, setIndicators] = useState<
    Record<string, Record<string, boolean>>
  >(() => {
    const initial: Record<string, Record<string, boolean>> = {};
    agentsList.forEach((agent) => {
      let hash = 0;
      for (let i = 0; i < agent.id.length; i++) {
        hash = agent.id.charCodeAt(i) + ((hash << 5) - hash);
      }
      const check = (index: number, threshold: number) => {
        return Math.abs((hash >> index) % 10) < threshold;
      };

      initial[agent.id] = {
        overtime: agent.stats.highPriorityTasks > 2 || check(0, 3),
        no_timeoff: check(1, 4),
        task_overload: agent.stats.pendingTasks > 3 || check(2, 3),
        isolation: agent.stats.kudosReceived === 0 || check(3, 2),
        blockers: check(4, 3),
        weekend: check(5, 2),
      };
    });
    return initial;
  });

  const getIndicatorsForSelected = () => {
    return (
      indicators[selectedAgentId] || {
        overtime: selectedAgent.stats.highPriorityTasks > 3,
        no_timeoff: false,
        task_overload: selectedAgent.stats.pendingTasks > 4,
        isolation: selectedAgent.stats.kudosReceived === 0,
        blockers: false,
        weekend: false,
      }
    );
  };

  const toggleIndicator = (key: string) => {
    const current = getIndicatorsForSelected();
    setIndicators({
      ...indicators,
      [selectedAgentId]: {
        ...current,
        [key]: !current[key],
      },
    });
  };

  const activeIndicators = getIndicatorsForSelected();
  const checkedCount = Object.values(activeIndicators).filter(Boolean).length;
  const maxScore = 6;
  const stressPercentage = Math.round((checkedCount / maxScore) * 100);

  const getStressLevel = (percentage: number) => {
    if (percentage <= 20)
      return {
        label: "Salubre",
        color: "text-emerald-500",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
        icon: <Smile className="h-5 w-5 text-emerald-500" />,
      };
    if (percentage <= 50)
      return {
        label: "Osservazione",
        color: "text-amber-500",
        bg: "bg-amber-500/10",
        border: "border-amber-500/20",
        icon: <Activity className="h-5 w-5 text-amber-500" />,
      };
    if (percentage <= 80)
      return {
        label: "Rischio Elevato",
        color: "text-orange-500",
        bg: "bg-orange-500/10",
        border: "border-orange-500/20",
        icon: <AlertTriangle className="h-5 w-5 text-orange-500" />,
      };
    return {
      label: "Allarme Burnout",
      color: "text-rose-500 font-bold",
      bg: "bg-rose-500/10",
      border: "border-rose-500/20",
      icon: <Flame className="h-5 w-5 text-rose-500 animate-pulse" />,
    };
  };

  const level = getStressLevel(stressPercentage);

  // Dynamic recommendations text
  const getAiRecommendation = () => {
    if (stressPercentage === 0) {
      return "Il collaboratore mostra indicatori ottimali di bilanciamento vita-lavoro. Le sue riserve energetiche e il morale sono stabili.";
    }
    if (stressPercentage <= 20) {
      return "Indicatori nella norma. Considera di premiare il buon bilanciamento e mantieni sessioni di allineamento 1-on-1 ordinarie.";
    }
    if (stressPercentage <= 50) {
      return "Fattori di affaticamento in aumento. Si raccomanda di ridurre leggermente il carico di task critici ed evitare assegnazioni straordinarie nel weekend.";
    }
    if (stressPercentage <= 80) {
      return "Rischio burnout significativo rilevato. È caldamente consigliato concordare 2-3 giorni di ferie compensative, effettuare un audit dei blocker e delegare ad altri membri i compiti secondari.";
    }
    return "SOGLIA DI BURNOUT RAGGIUNTA. Si consiglia intervento urgente delle Risorse Umane: blocco totale di nuovi task, stop immediato agli straordinari, ferie riabilitative pianificate d'ufficio e redistribuzione strategica del portfolio progetti.";
  };

  return (
    <div className="space-y-6">
      {/* Team stress bird's eye view */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {agentsList.map((agent) => {
          const agentIndicators = indicators[agent.id] || {
            overtime: agent.stats.highPriorityTasks > 3,
            no_timeoff: false,
            task_overload: agent.stats.pendingTasks > 4,
            isolation: agent.stats.kudosReceived === 0,
            blockers: false,
            weekend: false,
          };
          const count = Object.values(agentIndicators).filter(Boolean).length;
          const pct = Math.round((count / 6) * 100);
          const lvl = getStressLevel(pct);

          return (
            <Card
              key={agent.id}
              onClick={() => setSelectedAgentId(agent.id)}
              className={`cursor-pointer transition-all border ${
                selectedAgentId === agent.id
                  ? "border-primary-500 ring-2 ring-primary-500/10"
                  : "border-border hover:border-text-secondary"
              } bg-card`}
            >
              <CardContent className="p-4 flex justify-between items-center">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-text-primary">
                    {agent.name}
                  </h4>
                  <p className="text-[10px] text-text-secondary font-medium">
                    {agent.role}
                  </p>
                </div>
                <div className="text-right">
                  <Badge
                    className={`${lvl.bg} ${lvl.color} ${lvl.border} text-[10px] px-2 py-0.5 font-semibold`}
                  >
                    {lvl.label}
                  </Badge>
                  <p className="text-xs font-bold text-text-primary mt-1">
                    {pct}% stress
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main evaluation dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Checklist of indicators */}
        <Card className="lg:col-span-7 bg-card border border-border shadow-sm">
          <CardHeader className="pb-2 border-b border-border/50">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <UserCheck className="h-4.5 w-4.5 text-primary-500" />
              Indicatori di Stress per: {selectedAgent?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-xs text-text-secondary">
              Seleziona gli indicatori presenti nelle ultime 4 settimane per
              calcolare l&apos;indice di rischio burnout:
            </p>

            <div className="space-y-3">
              {/* Overtime */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.overtime}
                  onChange={() => toggleIndicator("overtime")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Carico di lavoro straordinario
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Lavora costantemente oltre 45 ore settimanali o copre turni
                    aggiuntivi frequenti.
                  </p>
                </div>
              </label>

              {/* No downtime */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.no_timeoff}
                  onChange={() => toggleIndicator("no_timeoff")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Assenza di periodi di riposo prolungati
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Nessuna richiesta di ferie o permesso approvata/richiesta
                    negli ultimi 6 mesi.
                  </p>
                </div>
              </label>

              {/* Task Overload */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.task_overload}
                  onChange={() => toggleIndicator("task_overload")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Accumulo di compiti urgenti e ad alta priorità
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Assegnatario di{" "}
                    {selectedAgent?.stats.highPriorityTasks || 0} task critici /
                    ad alto impatto ancora inevasi.
                  </p>
                </div>
              </label>

              {/* Isolation */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.isolation}
                  onChange={() => toggleIndicator("isolation")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Scarsa interazione e riconoscimento (Isolamento)
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Ha ricevuto solo {selectedAgent?.stats.kudosReceived || 0}{" "}
                    ringraziamenti (Kudos) o feedback peer-to-peer di recente.
                  </p>
                </div>
              </label>

              {/* Blockers */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.blockers}
                  onChange={() => toggleIndicator("blockers")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Esposizione a forti dipendenze esterne (Friction)
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Lavora in contesti bloccati da dipendenze esterne o problemi
                    organizzativi irrisolti.
                  </p>
                </div>
              </label>

              {/* Weekend work */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 hover:bg-card-muted/20 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={!!activeIndicators.weekend}
                  onChange={() => toggleIndicator("weekend")}
                  className="rounded border-border text-primary-600 focus:ring-primary-500 h-4.5 w-4.5 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-text-primary">
                    Operatività straordinaria nel fine settimana
                  </span>
                  <p className="text-[10px] text-text-secondary">
                    Attività di commit, log ore o invio email rilevata di sabato
                    o domenica.
                  </p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Right Column: AI Heatmeter and Mitigation panel */}
        <div className="lg:col-span-5 space-y-6">
          {/* Heatmeter */}
          <Card className="bg-card border border-border shadow-sm">
            <CardHeader className="pb-2 border-b border-border/50">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Flame className="h-4.5 w-4.5 text-orange-500 animate-pulse" />
                Stress Heatmeter
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 flex flex-col items-center space-y-6">
              {/* Circular gauge */}
              <div className="relative w-36 h-36 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="72"
                    cy="72"
                    r="60"
                    stroke="var(--border)"
                    strokeWidth="10"
                    fill="transparent"
                  />
                  <circle
                    cx="72"
                    cy="72"
                    r="60"
                    stroke={
                      stressPercentage <= 20
                        ? "#10b981"
                        : stressPercentage <= 50
                          ? "#f59e0b"
                          : stressPercentage <= 80
                            ? "#f97316"
                            : "#f43f5e"
                    }
                    strokeWidth="10"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 60}
                    strokeDashoffset={
                      2 * Math.PI * 60 * (1 - stressPercentage / 100)
                    }
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-0.5">
                  <span className="text-3xl font-black text-text-primary">
                    {stressPercentage}%
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">
                    Stress Index
                  </span>
                </div>
              </div>

              {/* Status Badge & details */}
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  {level.icon}
                  <h3 className={`text-base font-bold ${level.color}`}>
                    {level.label}
                  </h3>
                </div>
                <p className="text-xs text-text-secondary font-medium">
                  {checkedCount} su {maxScore} fattori di stress riscontrati.
                </p>
              </div>

              {/* Progress Line representation */}
              <div className="w-full space-y-1">
                <div className="flex justify-between text-[10px] text-text-secondary font-bold uppercase">
                  <span>Basso</span>
                  <span>Moderato</span>
                  <span>Critico</span>
                </div>
                <div className="h-2 w-full bg-border rounded-full overflow-hidden flex">
                  <div
                    className="bg-emerald-500 h-full"
                    style={{ width: "20%" }}
                  />
                  <div
                    className="bg-amber-500 h-full"
                    style={{ width: "30%" }}
                  />
                  <div
                    className="bg-orange-500 h-full"
                    style={{ width: "30%" }}
                  />
                  <div
                    className="bg-rose-500 h-full"
                    style={{ width: "20%" }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Recommendations panel */}
          <Card className="bg-card border border-border shadow-sm">
            <CardHeader className="pb-2 border-b border-border/50">
              <CardTitle className="text-sm font-bold flex items-center gap-1.5">
                <Sparkles className="h-4.5 w-4.5 text-indigo-500" />
                Copilota AI - Mitigazione Rischio
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="p-4 bg-indigo-500/[0.03] border border-indigo-500/10 rounded-lg space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-600">
                  <Info className="h-4 w-4" />
                  Raccomandazioni Generate in Tempo Reale
                </div>
                <p className="text-xs text-text-primary leading-relaxed font-medium">
                  {getAiRecommendation()}
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="w-full text-xs">
                  Invia Report al Collaboratore
                </Button>
                <Button size="sm" className="w-full text-xs">
                  Pianifica Check-in
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
